import type { Calendar } from "@event-calendar/core";
import { halfOpenOverlap } from "@pwz/domain";
import type { Instructor, ManagerCalendarEvent, Venue } from "../../../api/schemas.js";
import {
  addMinutes,
  formatTimeInZone,
  getDeviceTimeZone,
  toZonedIsoString,
} from "../../../lib/timezone.js";
import type { CapacitySnapshot } from "./types.js";

/**
 * Краска инструктора: бледная заливка держит тёмный текст (контраст не зависит от тона),
 * насыщенная кромка опознаёт человека с одного взгляда. Первым идёт фирменный синий,
 * дальше — соседние по кругу тона. Кирпичный и охра зарезервированы под статусы урока.
 */
export interface InstructorPaint {
  readonly stripe: string;
  readonly wash: string;
}

const INSTRUCTOR_PAINTS: readonly InstructorPaint[] = [
  { stripe: "#0e81f6", wash: "#dcecfe" },
  { stripe: "#8b5cf6", wash: "#e9e2fd" },
  { stripe: "#0d9488", wash: "#d9f0ed" },
  { stripe: "#d946a6", wash: "#fbdcf1" },
  { stripe: "#2563eb", wash: "#dde6fd" },
  { stripe: "#0891b2", wash: "#d8eef5" },
  { stripe: "#7c3aed", wash: "#e4dcfb" },
  { stripe: "#4f46e5", wash: "#e0dffa" },
];

const FALLBACK_PAINT: InstructorPaint = { stripe: "#5d6b8a", wash: "#e2e8f2" };

export function paintForInstructor(id: string): InstructorPaint {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return INSTRUCTOR_PAINTS[hash % INSTRUCTOR_PAINTS.length] ?? FALLBACK_PAINT;
}

/** Инициалы для кружка инструктора: «Ada Rider» → «AR», «Бен» → «Б». */
export function instructorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "?";
  const second = words[1]?.charAt(0) ?? "";
  return (first + second).toUpperCase();
}

export function computeCapacitySnapshots(
  events: readonly ManagerCalendarEvent[],
): CapacitySnapshot[] {
  const byVenue = new Map<string, ManagerCalendarEvent[]>();
  for (const event of events) {
    if (!event.venueId || !event.startTime || event.status === "canceled") {
      continue;
    }
    const list = byVenue.get(event.venueId) ?? [];
    list.push(event);
    byVenue.set(event.venueId, list);
  }

  const snapshots: CapacitySnapshot[] = [];
  for (const [venueId, list] of byVenue) {
    for (const event of list) {
      const startMs = Date.parse(event.startTime);
      const endMs = event.endTime
        ? Date.parse(event.endTime)
        : startMs + event.durationMinutes * 60_000;
      let occupied = 0;
      for (const other of list) {
        const oStart = Date.parse(other.startTime);
        const oEnd = other.endTime
          ? Date.parse(other.endTime)
          : oStart + other.durationMinutes * 60_000;
        // Та же формула, по которой сервер отказывает в записи (`@pwz/domain`): своя копия
        // расходилась бы с ней, и интерфейс показывал бы «свободно» там, где сервер даёт 409.
        if (halfOpenOverlap({ startMs: oStart, endMs: oEnd }, { startMs, endMs })) {
          occupied += 1;
        }
      }
      snapshots.push({
        venueId,
        startMs,
        endMs,
        occupied,
        capacity: event.venueCapacity ?? 1,
      });
    }
  }
  return snapshots;
}

export function capacityForEvent(
  event: ManagerCalendarEvent,
  snapshots: readonly CapacitySnapshot[],
): { occupied: number; capacity: number } {
  if (!event.venueId) {
    return { occupied: 1, capacity: 1 };
  }
  const startMs = Date.parse(event.startTime);
  const endMs = event.endTime
    ? Date.parse(event.endTime)
    : startMs + event.durationMinutes * 60_000;
  const match = snapshots.find(
    (s) => s.venueId === event.venueId && s.startMs === startMs && s.endMs === endMs,
  );
  return {
    occupied: match?.occupied ?? 1,
    capacity: match?.capacity ?? event.venueCapacity ?? 1,
  };
}

export function toCalendarResources(instructors: readonly Instructor[]): Calendar.ResourceInput[] {
  return instructors.map((instructor) => ({
    id: instructor.id,
    title: instructor.name,
  }));
}

export function toCalendarEvents(
  events: readonly ManagerCalendarEvent[],
  snapshots: readonly CapacitySnapshot[],
  instructorColors: ReadonlyMap<string, InstructorPaint> = new Map(),
  displayTimeZone = getDeviceTimeZone(),
): Calendar.EventInput[] {
  return events.map((event) => {
    const start = new Date(event.startTime);
    const end = event.endTime ? new Date(event.endTime) : addMinutes(start, event.durationMinutes);
    const cap = capacityForEvent(event, snapshots);
    const over = cap.occupied > cap.capacity;
    const venueTz = event.venueTimezone ?? displayTimeZone;
    // Время сетки, а не устройства: календарь стоит в поясе площадки, и карточка обязана
    // читаться так же — иначе подпись и слот, в котором она лежит, говорят разное.
    const displayTime = formatTimeInZone(start, displayTimeZone);
    const displayEndTime = formatTimeInZone(end, displayTimeZone);
    const venueTime = formatTimeInZone(start, venueTz);
    const titleParts = [
      event.clientName ?? "Lesson",
      event.courseCode ? `· ${event.courseCode}` : null,
      `#${event.lessonOrder}`,
      event.venueName ? `@ ${event.venueName}` : null,
      `${cap.occupied}/${cap.capacity}`,
    ].filter(Boolean);
    const classNames = [
      "pwz-event",
      `pwz-event--${event.status}`,
      over ? "pwz-event--over-capacity" : "",
    ].filter(Boolean);
    const paint = event.instructorId ? instructorColors.get(event.instructorId) : undefined;

    return {
      id: event.lessonId,
      start: toZonedIsoString(start, displayTimeZone),
      end: toZonedIsoString(end, displayTimeZone),
      title: titleParts.join(" "),
      resourceIds: event.instructorId ? [event.instructorId] : [],
      editable: event.status === "scheduled",
      startEditable: event.status === "scheduled",
      durationEditable: event.status === "scheduled",
      classNames,
      // Заливка и кромка — инструктор; статусы перебивают её в CSS (.pwz-event--*).
      ...(paint ? { backgroundColor: paint.wash, borderColor: paint.stripe } : {}),
      extendedProps: {
        lesson: event,
        capacityOccupied: cap.occupied,
        capacityTotal: cap.capacity,
        displayTime,
        displayEndTime,
        venueTime,
        displayTimeZone,
        venueTimeZone: venueTz,
        // Кружок инструктора рисуется прямо в карточке — цвет и инициалы берём здесь.
        instructorInitials: event.instructorName ? instructorInitials(event.instructorName) : null,
        instructorStripe: paint?.stripe ?? null,
      },
    };
  });
}

export function filterEventsByVenues(
  events: readonly ManagerCalendarEvent[],
  venueIds: readonly string[],
): ManagerCalendarEvent[] {
  if (venueIds.length === 0) {
    return [...events];
  }
  const set = new Set(venueIds);
  return events.filter((e) => e.venueId !== null && set.has(e.venueId));
}

export function mergeVenueLookup(venues: readonly Venue[]): Map<string, Venue> {
  return new Map(venues.map((v) => [v.id, v]));
}
